import Toast from 'react-native-toast-message';
import axios, {CancelTokenSource} from 'axios';
import {makeAutoObservable, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import {ERROR_MESSAGE} from '../components/ToastBottom';
import {config} from '../config';
import {DAYS_OF_THE_WEEK} from '../constants/days';
import {AppRoute} from '../models/AppRoute';
import {GeocodedSuburb, Shop, StoreByDistance} from '../models/middleware';
import {StoreType} from '../navigation/routes/types';
import apiService from '../transport/apiService';
import {logError} from '../utils/errorLogging';
import {storeByDistanceToShop} from '../utils/giftCards';
import {
  getValueFromStorage,
  removeValueFromStorage,
  setValueToStorage,
} from '../utils/localStorage';
import {logger, serializeError} from '../utils/logger';
import {getStoreType} from '../utils/store';

import type {RootStore} from './rootStore';

const SHOP_SALESFORCE_STORAGE_KEY = 'SHOP_SALESFORCE_STORAGE_KEY';
const LIQUOR_SHOP_SALESFORCE_STORAGE_KEY = 'LIQUOR_SHOP_SALESFORCE_STORAGE_KEY';

const DEFAULT_MAX_DISTANCE_KM = 200;

// Poor man's request queue
let reqCount = 0;
let cancelToken: CancelTokenSource;

export type PreviousStoreSnapshot = {
  store: Shop;
  ecommerceDisabled: boolean;
  canDeliver: boolean;
  canPickup: boolean;
  loyaltyEnabled: boolean;
};

export class ShopSelectionStore {
  rootStore: RootStore;
  // If the user used a deep link like http://localhost:7050/store/C000130552,
  // shopIdSetInDeepLink is set to the shop/store id (C000130552 in this example)
  shopIdSetInDeepLink: string | null = null;
  shopSalesforceId: string | null = null;
  // Snapshot of the store the user was on before switching into the gift-card flow.
  // We persist the full store-level state (not just the Shop) because the MFL response
  // shape doesn't reliably carry delivery_available/pickup_available flags — those live
  // on canDeliver/canPickup on the store instance — so restoring from a bare Shop causes
  // selectStore() to mis-route the previous ecommerce store through the darksite path.
  previouslySelectedStore: PreviousStoreSnapshot | null = null;
  selectedStore: Shop | null = null;
  canPickup = false;
  canDeliver = false;
  private _loyaltyEnabled = false;
  selectedSuburb: GeocodedSuburb | null | undefined = null;
  suburbList: GeocodedSuburb[] = [];
  storeList: Shop[] = [];
  maxDistance = DEFAULT_MAX_DISTANCE_KM; // km
  liquorLicense = null;
  storeListLoading = true;
  // Can they create orders etc...
  ecommerceDisabled = false;
  // Looking up geocoding
  geocoding = false;
  storeLoading = true;
  suburbListLoading = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    makeLoggable(this);
  }

  get loyaltyEnabled() {
    /**
     * Dev shops need to be hardcoded because they are not in airtable where loyalty enabled info is.
     */
    const hardcodedLoyaltyEnabledShops = [
      // App development shop sydney
      '5dd328e4ec28667bb1004515',
      // App development shop brisbane
      '5dd32850ec28667ba8004a24',
      // App development Harry Brown
      '67073ba596dfb2d5271b92f5',
      // App development SnS
      '6707e67efc85738000abc378',
      // App development TC WA Shop
      '670739a52ff84b32dbe5c7c2',
    ];

    return (
      (this.selectedStore?.id && hardcodedLoyaltyEnabledShops.includes(this.selectedStore.id)) ||
      this._loyaltyEnabled
    );
  }

  reset = () => {};

  setLoading = (value: boolean) => (this.storeLoading = value);

  clearShopSalesforceIdFromStorage = () => removeValueFromStorage(SHOP_SALESFORCE_STORAGE_KEY);

  clearLiquorShopSalesforceIdFromStorage = () =>
    removeValueFromStorage(LIQUOR_SHOP_SALESFORCE_STORAGE_KEY);

  loadShopSalesforceIdFromLocalStorage = async () =>
    getValueFromStorage(SHOP_SALESFORCE_STORAGE_KEY);

  setShopSalesforceIdToStorage = (salesforceId: any) => {
    if (salesforceId) {
      setValueToStorage(SHOP_SALESFORCE_STORAGE_KEY, salesforceId);
    } else {
      this.clearShopSalesforceIdFromStorage();
    }
  };

  loadLiquorShopSalesforceIdFromLocalStorage = async () =>
    getValueFromStorage(LIQUOR_SHOP_SALESFORCE_STORAGE_KEY);

  setLiquorShopSalesforceIdToStorage = (salesforceId: any) => {
    if (salesforceId) {
      setValueToStorage(LIQUOR_SHOP_SALESFORCE_STORAGE_KEY, salesforceId);
    } else {
      this.clearLiquorShopSalesforceIdFromStorage();
    }
  };

  findSalesforceShop = async (shopSFId: string) => {
    let isDarkStore = false;
    let storeFound = false;

    try {
      // We have a salesforce ID for the store that the user has previously selected.
      // retrieve the shop from MFL using the salesforce ID
      try {
        const stores = await apiService.findSalesforceMatch(shopSFId);
        if (stores.length === 1) {
          // MFL has a match, check the capabilities of the store and
          // pass on to store chooser.
          const mflShop = stores[0];
          apiService.setShopId(mflShop.id);
          apiService.setStoreNumber(shopSFId); // DIS-1330 competition entry gate
          apiService.getOpenDeliveryTimes().then((deliveries: any) => {
            if (deliveries.length) runInAction(() => (this.canDeliver = true));
          });
          apiService.getOpenPickupTimes().then((pickups: any) => {
            if (pickups.length) runInAction(() => (this.canPickup = true));
          });
          this.selectStoreFromLoad(shopSFId, mflShop);
          storeFound = true;
        } else {
          logger.warn(
            'ShopSelectionStore',
            'MFL returned multiple stores, treating as dark store',
            {
              shopSFId,
              storesCount: stores.length,
            },
          );
          isDarkStore = true;
        }
      } catch (error: any) {
        if (error?.response?.status === 404) {
          logger.info('ShopSelectionStore', 'Store not found in MFL (404), trying darksite', {
            shopSFId,
          });
          isDarkStore = true;
        } else throw error;
      }

      if (isDarkStore) {
        // MFL does not have a match! Defer to darksite load.
        // Load the matching store from Airtable Salesforce Members Data
        const darksiteStore = await apiService.searchShopBySalesforceId(shopSFId);
        await this.selectDarksiteStore(darksiteStore);
        storeFound = true;
      }
    } catch (error: any) {
      logError(error, `ShopSelectionStore.retrieveShopData(${shopSFId})`);
      runInAction(() => (this.storeLoading = false));
    } finally {
      // Ensure storeLoading is set to false if we didn't find a store
      // (selectStoreFromLoad and selectDarksiteStore set it to false on success)
      if (!storeFound) {
        runInAction(() => (this.storeLoading = false));
      }
    }
  };

  // Storage calls
  // This is loading a shop when the user is opening the app
  // and they've already selected a store. We only store the
  // salesforce ID, so we retrieve the store details from MFL.
  retrieveShopData = async () => {
    try {
      const shopSFId = await this.loadShopSalesforceIdFromLocalStorage();
      if (shopSFId) {
        runInAction(() => {
          this.shopSalesforceId = shopSFId;
        });
        // We have a salesforce ID for the store that the user has previously selected.
        // retrieve the shop from MFL using the salesforce ID
        await this.findSalesforceShop(shopSFId);
      } else {
        logger.debug('ShopSelectionStore', 'No shop Salesforce ID found in storage');
        runInAction(() => (this.storeLoading = false));
      }
    } catch (error: any) {
      logger.error('ShopSelectionStore', 'Error retrieving store details from storage', {
        errorCode: error.code || 'NO_CODE',
        error,
      });
      runInAction(() => (this.storeLoading = false));
    }
  };

  reverseGeocode = async (lat: number, lon: number) => {
    runInAction(() => (this.geocoding = true));
    try {
      const suburbs = await apiService.reverseGeocode(lat, lon);
      runInAction(() => {
        logger.debug('ShopSelectionStore', 'Reverse geocode completed', {
          suburbsCount: suburbs?.length,
        });
        this.suburbList = suburbs;
        this.geocoding = false;
        // if (suburbs.length === 1) {
        //   //  Exactly 1 suburb returned, select that.
        //   runInAction(() => {
        //     this.resetLoading();
        //     this.selectSuburb(suburbs[0]);
        //   });
        // }
      });
      return suburbs;
    } catch (err) {
      logger.error('ShopSelectionStore', 'Error during reverse geocoding', serializeError(err));
      return [];
    }
  };

  async loadSuburbList(searchText: string) {
    runInAction(() => {
      this.suburbListLoading = true;
      this.maxDistance = DEFAULT_MAX_DISTANCE_KM;
    });

    const reqIndex = ++reqCount;
    try {
      //Check if there are any previous pending requests
      if (typeof cancelToken !== typeof undefined) {
        cancelToken.cancel('Operation canceled due to new request.');
      }

      //Save the cancel token for the current request
      cancelToken = axios.CancelToken.source();

      if (!!searchText && searchText.length >= 3) {
        const res = await apiService.searchSuburbs(searchText, cancelToken);
        if (!!res && res.length > 0 && reqIndex === reqCount) {
          runInAction(() => {
            this.suburbList = res;
          });
        }
      } else {
        this.suburbList = [];
      }
    } catch (err) {
      logger.error('ShopSelectionStore', 'Error finding stores by suburb', serializeError(err));
    } finally {
      if (reqIndex >= reqCount) {
        runInAction(() => (this.suburbListLoading = false));
      }
    }
  }

  async querySuburbStores(suburb: GeocodedSuburb, maxDistance: number) {
    if (!suburb?.locality || !suburb?.lat || !suburb?.lon) {
      throw new Error('Suburb lat/lon not found');
    }

    const postcode = suburb?.postal_code;
    const suburbName = suburb?.locality;

    let shopList: Shop[];

    // Dev needs to load shops directly from MFL
    if (this.rootStore.settingsStore.showAppDevelopmentShop) {
      shopList = await apiService.searchShopsBySuburbPostcodeLatLon(
        suburbName,
        postcode,
        suburb.lat,
        suburb.lon,
        1000,
      );
    } else {
      // The shops in this list have already set their pickup and delivery
      // capabilities, `pickup_available` and `delivery_available`
      // (delivery_available as interpreted as "can deliver to this suburb (suburbName)").
      shopList = await apiService.searchShopsBySuburbPostcodeLatLon(
        suburbName,
        postcode,
        suburb.lat,
        suburb.lon,
        maxDistance,
      );
    }

    return shopList;
  }

  /**
   * DIS-1501-B (flag FF_GIFT_CARD_ALL_STORES). Fetch the tenant's ENTIRE store
   * list from GET /members/stores/:tenant, sorted by distance from the searched
   * suburb, page-capped by MAX_PAGES as a safety net. suburb/postcode drive the
   * middleware delivery enrich; no `state` filter (we want all brand stores, not
   * just same-state). Pages on `hasMore`. Returns adapted Shops (endpoint order =
   * distance ASC).
   */
  async queryAllTenantStores(suburb: GeocodedSuburb): Promise<Shop[]> {
    if (!suburb?.locality || !suburb?.lat || !suburb?.lon) {
      throw new Error('Suburb lat/lon not found');
    }

    const tenant = config.BASE_TENANT;
    const limit = 500;
    const MAX_PAGES = 20; // hard cap: 20 * 500 = 10k stores, far above any brand
    const items: StoreByDistance[] = [];
    let offset = 0;
    let hasMore = true;
    let pages = 0;

    // Terminate on `hasMore`; MAX_PAGES is the safety net against a misbehaving
    // endpoint that returns hasMore:true forever (B1 pagination isn't written yet).
    while (hasMore && pages < MAX_PAGES) {
      const page = await apiService.getStoresByTenant(tenant, {
        lat: suburb.lat,
        lon: suburb.lon,
        suburb: suburb.locality,
        postcode: suburb.postal_code,
        offset,
        limit,
      });
      items.push(...page.items);
      hasMore = page.hasMore;
      offset += page.items.length;
      pages += 1;
    }

    if (hasMore) {
      logger.warn('ShopSelectionStore', 'queryAllTenantStores hit MAX_PAGES; list truncated', {
        tenant,
        fetched: items.length,
      });
    }

    return items.map(storeByDistanceToShop);
  }

  async selectSuburb(suburb?: GeocodedSuburb | null) {
    if (suburb) {
      this.selectedSuburb = suburb;
    }
    // @ts-expect-error - Type mismatch needs investigation
    suburb = suburb || {...this.selectedSuburb};
    if (!suburb) {
      throw new Error('Suburb is required');
    }

    let shopList: Shop[];
    try {
      shopList = await this.querySuburbStores(suburb, this.maxDistance);

      runInAction(() => {
        this.storeList = shopList;
        this.storeListLoading = false;
      });
    } catch (error) {
      logger.error('ShopSelectionStore', 'Error finding stores by postcode', {error});
    }
  }

  expandSearchRange() {
    runInAction(() => {
      this.maxDistance = this.maxDistance + DEFAULT_MAX_DISTANCE_KM;
    });
  }

  clearSuburbList() {
    runInAction(() => {
      this.suburbList = [];
    });
  }

  loadLiquorLicense = async () => {
    try {
      const data = await apiService.getLiquorLicense(this.shopSalesforceId);
      runInAction(() => {
        this.liquorLicense = data || null;
      });
    } catch (error) {
      logger.error('ShopSelectionStore', 'Unable to retrieve liquor license', {error});
    }
  };

  resetLoading() {
    runInAction(() => (this.storeListLoading = true));
  }

  isStoreListLoading() {
    return this.storeListLoading;
  }

  async selectPreviousStore() {
    if (!this.previouslySelectedStore) {
      // else, suburb/store selector
      return;
    }
    const snapshot = this.previouslySelectedStore;
    const previousStore: Shop = {...snapshot.store};

    if (this.rootStore.settingsStore.showAppDevelopmentShop) {
      previousStore.pickup_available = true;
      previousStore.delivery_available = true;
    } else {
      // Restore the selection gate inputs from the snapshot — the MFL shape on
      // selectedStore doesn't carry these flags, which is why restoring from a bare
      // Shop was routing through selectDarksiteStore and flipping ecommerceDisabled.
      previousStore.delivery_available = snapshot.canDeliver || previousStore.delivery_available;
      previousStore.pickup_available = snapshot.canPickup || previousStore.pickup_available;
    }

    if (snapshot.ecommerceDisabled) {
      await this.selectDarksiteStore(previousStore);
    } else {
      await this.selectStore(previousStore);
    }
  }

  updateZohoStoreInfo = () => {
    try {
      if (window?.$zoho?.salesiq?.visitor && this.selectedStore) {
        window.$zoho.salesiq.visitor.info({
          'Store Name': this.selectedStore.name,
          'Store Number': this.selectedStore.store_number || '',
          'Store ID': this.selectedStore.id || '',
        });
      }
    } catch (error) {
      logError(error, 'ShopSelectionStore.updateZohoStoreInfo');
    }
  };

  async selectStore(store: Shop) {
    this.rootStore.globalStore.setIsSwitchingStore(true);
    const previousStore = this.selectedStore;

    runInAction(() => {
      // Guard: don't overwrite previouslySelectedStore with a gift card store —
      // we need the real liquor store preserved for restoration after gift card flow
      const isCurrentStoreGiftCard = getStoreType(previousStore) === StoreType.GiftCardStore;
      if (!isCurrentStoreGiftCard) {
        this.previouslySelectedStore = previousStore
          ? {
              store: previousStore,
              ecommerceDisabled: this.ecommerceDisabled,
              canDeliver: this.canDeliver,
              canPickup: this.canPickup,
              loyaltyEnabled: this._loyaltyEnabled,
            }
          : null;
      }
      this.storeLoading = true;
      this.maxDistance = DEFAULT_MAX_DISTANCE_KM;
    });

    if (!store.store_number) {
      logger.error('ShopSelectionStore', 'Shop has no Store Number', {
        shopSFId: store.salesforce_id,
      });
      return;
    }
    try {
      // In store shopping only, redirect to darksite
      if (!store.delivery_available && !store.pickup_available) {
        this.selectDarksiteStore(store);
      } else {
        // Coming in from the store chooser, need to look up the mfl shop
        const stores = await apiService.findSalesforceMatch(store.store_number);
        if (stores.length === 0) {
          throw new Error(`No stores returned by eCommerce for ${store?.store_number}`);
        }

        apiService.setShopId(stores[0].id);
        apiService.setStoreNumber(store.store_number); // DIS-1330 competition entry gate
        apiService.clearDepartment();

        runInAction(() => {
          this.setShopSalesforceIdToStorage(store.store_number);
          this.shopSalesforceId = store.store_number;
          this.selectedStore = stores[0];

          this.ecommerceDisabled = false;
          this.rootStore.globalStore.acceptedInstoreShopping(false);
          // Use API response values — input `store` may be a placeholder with incorrect flags
          this.canDeliver = stores[0].delivery_available ?? store.delivery_available;
          this.canPickup = stores[0].pickup_available ?? store.pickup_available;
          this._loyaltyEnabled = stores[0].loyalty_enabled ?? store.loyalty_enabled;
          this.storeLoading = false;
        });
        this.updateZohoStoreInfo();
      }
      await this.rootStore.initialiseSelectedStoreData();
      this.rootStore.globalStore.setIsSwitchingStore(false);
      this.rootStore.resetOffersStore(); // Offers can be store specific
    } catch (error: any) {
      logger.error('ShopSelectionStore', 'Select store error', {error});
      Toast.show({
        type: ERROR_MESSAGE,
        text1: `There was a problem setting your store to ${store?.name}. Please try again.`,
        visibilityTime: 10000,
      });
    }
  }

  // App reload, loading store from local storage cues
  selectStoreFromLoad(storeId: string | null, mflShop: Shop) {
    runInAction(() => {
      this.setShopSalesforceIdToStorage(storeId);
      this.shopSalesforceId = storeId;
      this.selectedStore = mflShop;
      this.ecommerceDisabled = false;
      this._loyaltyEnabled = mflShop.loyalty_enabled;
      this.storeLoading = false;
    });
    this.updateZohoStoreInfo();
  }

  selectDarksiteStore = async (store: Shop) => {
    try {
      const darksiteId = await apiService.getDarksiteId(store.store_number);
      const stores = await apiService.findSalesforceMatch(darksiteId);

      // Make sure the darksite store is active in MFL
      if (stores.length === 0) {
        throw new Error(`No stores returned by eCommerce for ${store?.store_number}`);
      }

      const regularHours: any = {};
      DAYS_OF_THE_WEEK.forEach(day => {
        const from = store.regular_hours?.[day].from;
        const until = store.regular_hours?.[day].until;
        if (from && until) {
          regularHours[day] = {
            from,
            until,
          };
        }
      });
      const munged = {
        ...stores[0],
        ...store,
        id: stores[0].id,
      };

      runInAction(() => {
        this.setShopSalesforceIdToStorage(store.store_number);
        this.shopSalesforceId = store.store_number;
        this._loyaltyEnabled = store.loyalty_enabled;
        this.selectedStore = munged;
        apiService.setShopId(munged.id);
        apiService.setStoreNumber(store.store_number); // DIS-1330 competition entry gate
        apiService.clearDepartment();
        this.ecommerceDisabled = true;
        this.rootStore.globalStore.acceptedInstoreShopping(false);
        this.storeLoading = false;
      });
    } catch (error: any) {
      logger.error('ShopSelectionStore', 'Airtable darksite error', {error});
      runInAction(() => {
        this.shopSalesforceId = null;
        this.storeLoading = false;
      });
      throw error;
    }
  };

  setCanDeliver = (value: boolean) => (this.canDeliver = value);

  setCanPickup = (value: boolean) => (this.canPickup = value);

  setShopIdSetInDeepLink = (value: string | null) => (this.shopIdSetInDeepLink = value);

  // Util - converted to computed properties to avoid setState-during-render in React 19
  get selectedStoreType() {
    return getStoreType(this.selectedStore);
  }

  get isSelectedStoreGiftCardStore() {
    return this.selectedStoreType === StoreType.GiftCardStore;
  }

  get homeAppRoute() {
    return this.isSelectedStoreGiftCardStore ? AppRoute.GIFT_CARDS_MAIN : AppRoute.HOME;
  }

  get selectedStoreSummary() {
    if (!this.shopSalesforceId || !this.selectedStore) {
      return null;
    }
    return {
      id: this.shopSalesforceId,
      name: this.selectedStore.name,
    };
  }
}
